import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { debug } from "@/lib/debug";
import { buildCanonicalAuthUrl } from "@/lib/authHost";
import { setSentryUser } from "@/lib/sentry";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isInitialized: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Development logging helper
const logAuthEvent = (event: AuthChangeEvent, session: Session | null) => {
  debug.log(`[Auth] Event: ${event}`, {
    hasSession: !!session,
    userId: session?.user?.id?.slice(0, 8),
    expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
  });
};

/**
 * The session supabase-js persisted, read straight off disk.
 *
 * Only used when getSession() fails or stalls — see the watchdog below. It is a
 * fallback for rendering, not a substitute for the client's own state: supabase-js
 * still holds the same tokens and will refresh them itself.
 *
 * An expired token is treated as no session at all. Handing one back would render the
 * member as signed in and then fail every request they made.
 */
const readStoredSession = (): Session | null => {
  try {
    const key = Object.keys(localStorage).find((k) => /^sb-.*-auth-token$/.test(k));
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const session: Session | null = parsed?.access_token ? parsed : parsed?.currentSession ?? null;
    if (!session?.access_token || !session?.user) return null;
    if (session.expires_at && session.expires_at * 1000 <= Date.now()) return null;
    return session;
  } catch {
    // A corrupted entry is exactly the kind of thing that got us here. Ignore it.
    return null;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Memoized session update handler
  const handleSessionUpdate = useCallback((newSession: Session | null, event?: AuthChangeEvent) => {
    if (event) {
      logAuthEvent(event, newSession);
    }
    setSession(newSession);
    setUser(newSession?.user ?? null);
    setSentryUser(newSession?.user ? { id: newSession.user.id, email: newSession.user.email } : null);
    setLoading(false);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        // Handle different auth events
        switch (event) {
          case 'SIGNED_IN':
            // CRITICAL FIX: Supabase fires SIGNED_IN on EVERY tab switch
            // (visibilitychange -> visible -> _recoverAndRefresh -> _notifyAllSubscribers).
            // If we call handleSessionUpdate() here, it creates a NEW user object reference
            // every time, which triggers useEffect cascades in VisionAssistant, BrandContext,
            // etc., causing the active conversation to reset to the welcome screen.
            // Solution: Stabilize references just like TOKEN_REFRESHED - only update if
            // the user actually changed (different ID = real sign-in vs. tab-switch echo).
            if (session) {
              logAuthEvent(event, session);
              setSession(prev => {
                if (prev?.access_token === session.access_token) return prev;
                return session;
              });
              setUser(prev => {
                if (prev?.id === session.user?.id) return prev; // Same user, keep old reference
                return session.user;
              });
              setLoading(false);
              setIsInitialized(true);
            } else {
              // session is null on SIGNED_IN - shouldn't happen, but handle gracefully
              handleSessionUpdate(null, event);
            }
            break;
          case 'USER_UPDATED':
            handleSessionUpdate(session, event);
            break;
          case 'TOKEN_REFRESHED':
            // Update session silently without triggering loading state
            // CRITICAL: Stabilize BOTH session AND user references to prevent
            // cascade: new ref → re-renders → BrandContext refetch → loading flash → data loss
            if (session) {
              logAuthEvent(event, session);
              setSession(prev => {
                // Only create a new reference if the access token actually changed
                if (prev?.access_token === session.access_token) return prev;
                return session;
              });
              setUser(prev => {
                if (prev?.id === session.user?.id) return prev; // Keep same reference
                return session.user;
              });
            }
            break;
          case 'SIGNED_OUT':
            handleSessionUpdate(null, event);
            break;
          case 'INITIAL_SESSION':
            handleSessionUpdate(session, event);
            break;
          default:
            handleSessionUpdate(session, event);
        }
      }
    );

    // THEN check for existing session.
    //
    // `loading` starts true and is only ever cleared from a resolved getSession() or an
    // auth event. That made this call a single point of failure: if it rejected, or
    // simply never settled, the app sat on its loading screen for ever with no error.
    //
    // It can do exactly that. supabase-js serialises token refreshes behind the Web
    // Locks API, so a second window or tab on the same origin — an installed PWA
    // alongside a browser tab, or a previous instance that did not shut down cleanly —
    // can hold the lock and leave this promise pending indefinitely. A signed-out
    // visitor never notices, because there is no token to refresh; a signed-in one is
    // stuck. That is the "loading for ever when I'm logged in" report.
    //
    // So: never let the first paint depend on this settling.
    let settled = false;
    const stopWaiting = (session: Session | null) => {
      if (settled || !mounted) return;
      settled = true;
      clearTimeout(watchdog);
      handleSessionUpdate(session);
    };

    const watchdog = setTimeout(() => {
      if (settled || !mounted) return;
      debug.warn('[Auth] getSession() has not settled — releasing the UI and falling back to the stored session');
      // Fall back to the session on disk rather than rendering as signed out, or the
      // member gets bounced to the login page while holding a perfectly good session.
      // onAuthStateChange still delivers the real one once the lock frees.
      stopWaiting(readStoredSession());
    }, 8000);

    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          debug.warn('[Auth] Error getting session:', error.message);
          stopWaiting(null);
        } else {
          stopWaiting(session);
        }
      })
      .catch((err) => {
        // A rejection here used to be unhandled, which left loading stuck on true.
        debug.warn('[Auth] getSession() threw:', err);
        stopWaiting(readStoredSession());
      });

    return () => {
      mounted = false;
      clearTimeout(watchdog);
      subscription.unsubscribe();
    };
  }, [handleSessionUpdate]);

  const signUp = async (email: string, password: string) => {
    const domainOk = await isEmailDomainResolvable(email);
    if (!domainOk) {
      return {
        error: new Error(
          "We could not reach that email domain. Please double-check the address — did you mean .com?",
        ),
      };
    }
    const redirectUrl = buildCanonicalAuthUrl("/brand-guide", window.location.origin);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isInitialized, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

const AUTH_FALLBACK: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  isInitialized: false,
  signUp: async () => ({ error: new Error("AuthProvider not mounted") }),
  signIn: async () => ({ error: new Error("AuthProvider not mounted") }),
  signOut: async () => {},
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.warn("[useAuth] Called outside AuthProvider — returning safe defaults. This is expected during HMR but should not happen in production.");
    return AUTH_FALLBACK;
  }
  return context;
}

// Uses Google's public DNS-over-HTTPS API to verify the email domain has MX
// records before signup, catching typos like .coim or .comg at source.
// Fails open (returns true) if the DNS API is unreachable.
async function isEmailDomainResolvable(email: string): Promise<boolean> {
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  const domain = email.slice(at + 1).trim();
  if (!domain) return false;
  try {
    const res = await fetch(
      `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`,
    );
    if (!res.ok) return true; // fail open — don't block signup if DNS API is down
    const data = await res.json() as { Status: number; Answer?: unknown[] };
    return data.Status === 0 && Array.isArray(data.Answer) && data.Answer.length > 0;
  } catch {
    return true; // fail open
  }
}
